import React, { useState, useMemo } from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/**
 * MultiSelect (controlled)
 * Props:
 *   options: [{ value, label, group? }]
 *   value: string[]  // selected values
 *   onChange: (string[]) => void
 *   placeholder: string
 *   testId: string
 *   emptyLabel: string
 *   disabled: boolean
 */
export function MultiSelect({
  options = [],
  value = [],
  onChange,
  placeholder = "Pilih...",
  testId = "multi-select",
  emptyLabel = "Tidak ada pilihan",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label || "").toLowerCase().includes(q));
  }, [options, query]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const opt of filtered) {
      const key = opt.group || "";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(opt);
    }
    return Array.from(map.entries());
  }, [filtered]);

  const selectedOptions = useMemo(
    () => options.filter((o) => value.includes(o.value)),
    [options, value]
  );

  const toggle = (val) => {
    if (value.includes(val)) {
      onChange(value.filter((v) => v !== val));
    } else {
      onChange([...value, val]);
    }
  };

  const removeOne = (val, e) => {
    e?.stopPropagation();
    onChange(value.filter((v) => v !== val));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-testid={`${testId}-trigger`}
          className="flex min-h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-white px-3 py-1.5 text-sm shadow-sm hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <div className="flex flex-wrap gap-1 flex-1 text-left">
            {selectedOptions.length === 0 ? (
              <span className="text-slate-400">{placeholder}</span>
            ) : (
              selectedOptions.map((o) => (
                <Badge
                  key={o.value}
                  variant="secondary"
                  className="bg-emerald-100 text-emerald-800 border border-emerald-200 gap-1 pr-1"
                  data-testid={`${testId}-chip-${o.value}`}
                >
                  <span className="truncate max-w-[200px]">{o.label}</span>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={`Hapus ${o.label}`}
                    onClick={(e) => removeOne(o.value, e)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") removeOne(o.value, e);
                    }}
                    className="rounded p-0.5 hover:bg-emerald-200 cursor-pointer inline-flex items-center justify-center"
                  >
                    <X className="h-3 w-3" />
                  </span>
                </Badge>
              ))
            )}
          </div>
          <ChevronsUpDown className="h-4 w-4 text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[--radix-popover-trigger-width] p-0"
        align="start"
        data-testid={`${testId}-content`}
      >
        <div className="p-2 border-b">
          <Input
            placeholder="Cari..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9"
            data-testid={`${testId}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-slate-400">{emptyLabel}</div>
          ) : (
            grouped.map(([group, opts]) => (
              <div key={group || "__"}>
                {group ? (
                  <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                    {group}
                  </div>
                ) : null}
                {opts.map((opt) => {
                  const selected = value.includes(opt.value);
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => toggle(opt.value)}
                      data-testid={`${testId}-option-${opt.value}`}
                      className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-slate-50 ${
                        selected ? "bg-emerald-50/60" : ""
                      }`}
                    >
                      <div
                        className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 ${
                          selected ? "bg-emerald-600 border-emerald-600 text-white" : "border-slate-300"
                        }`}
                      >
                        {selected && <Check className="h-3 w-3" />}
                      </div>
                      <span className="text-slate-800 truncate flex-1 text-left">{opt.label}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        {value.length > 0 && (
          <div className="px-2 py-2 border-t flex items-center justify-between">
            <span className="text-xs text-slate-500">{value.length} dipilih</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange([])}
              data-testid={`${testId}-clear`}
              className="h-7 text-xs text-slate-600 hover:text-red-600"
            >
              Hapus semua
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default MultiSelect;
